import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/authStore';
import { create } from 'zustand';
import {
  isDefinitiveAuthFailure,
  refreshAccessTokenSingleFlight,
  handleDefinitiveAuthFailure,
} from './api';

interface SocketState {
  status: 'connected' | 'reconnecting' | 'disconnected';
  setStatus: (s: SocketState['status']) => void;
  /**
   * Bumped exactly once every time connectSocket() mints a brand-new
   * Socket.IO client (not on every call — reusing an already-connected
   * socket must not bump it). AppLayout fully disconnects and reconnects
   * the socket whenever the access token changes (a deliberate CWE-613
   * mitigation, see connectSocket's `auth` callback below). Any other component that attaches persistent
   * listeners to `getSocket()` inside a useEffect MUST include `socketId`
   * in that effect's dependency array, or its handlers stay registered on
   * the old, now-dead socket after the first silent token refresh and
   * never fire again for the life of the component.
   */
  socketId: number;
  bumpSocketId: () => void;
}

export const useSocketStore = create<SocketState>((set) => ({
  status: 'disconnected',
  setStatus: (status) => set({ status }),
  socketId: 0,
  bumpSocketId: () => set((s) => ({ socketId: s.socketId + 1 })),
}));

let socket: Socket | null = null;

/**
 * Handshake rejections from the API's socket auth middleware
 * (packages/api/src/sockets/auth.middleware.ts). Socket.IO treats a
 * middleware `next(err)` as a fatal connect_error: `socket.active` goes
 * false and the client never retries on its own, so each one needs an
 * explicit recovery decision here.
 */
const HANDSHAKE_TOKEN_REJECTED = new Set(['Invalid token', 'Authentication required']);
const HANDSHAKE_MFA_REQUIRED = 'MFA_REQUIRED';
const HANDSHAKE_ACCOUNT_GONE = 'Account no longer active';

export const CONNECT_RETRY_BASE_MS = 1000;
export const CONNECT_RETRY_MAX_MS = 30_000;

let connectRetryTimer: ReturnType<typeof setTimeout> | null = null;
let connectRetryAttempt = 0;

function clearConnectRetry(): void {
  if (connectRetryTimer) {
    clearTimeout(connectRetryTimer);
    connectRetryTimer = null;
  }
  connectRetryAttempt = 0;
}

function scheduleConnectRetry(target: Socket): void {
  if (connectRetryTimer) return;
  const delay = Math.min(CONNECT_RETRY_BASE_MS * 2 ** connectRetryAttempt, CONNECT_RETRY_MAX_MS);
  connectRetryAttempt += 1;
  useSocketStore.getState().setStatus('reconnecting');
  connectRetryTimer = setTimeout(() => {
    connectRetryTimer = null;
    // The socket may have been torn down (logout / token swap) meanwhile.
    if (socket !== target) return;
    target.connect();
  }, delay);
}

/**
 * Recover from a handshake rejection instead of staying "Disconnected"
 * until some unrelated HTTP 401 happens to refresh the token. Exported so
 * the behaviour can be unit-tested without a live Socket.IO server.
 */
export async function handleConnectError(target: Socket, err: Error): Promise<void> {
  // `active` is true when Socket.IO's own reconnection loop is still
  // running (transport-level errors) — leave it alone and just surface it.
  if (target.active) {
    useSocketStore.getState().setStatus('reconnecting');
    return;
  }

  const reason = err?.message ?? '';

  if (reason === HANDSHAKE_MFA_REQUIRED) {
    // Nothing to retry until the user acts: MFA enrolment calls
    // reconnectSocket() once verify succeeds.
    useSocketStore.getState().setStatus('disconnected');
    return;
  }

  if (reason === HANDSHAKE_ACCOUNT_GONE) {
    // The account was disabled or deleted (e.g. an administrator offboarded
    // the user). Nothing will ever make this handshake succeed, and no HTTP
    // request necessarily follows on a long-lived page, so end the session
    // here: otherwise hasSession, cached messages, the profile and the E2EE
    // keys stay reachable indefinitely. Guarded like the refresh path so a
    // teardown that already happened is not repeated.
    useSocketStore.getState().setStatus('disconnected');
    if (socket === target && useAuthStore.getState().hasSession) {
      handleDefinitiveAuthFailure();
    }
    return;
  }

  if (HANDSHAKE_TOKEN_REJECTED.has(reason)) {
    // Stale access token (laptop woke after the 15-minute TTL, API deploy
    // bounced every socket). Refresh first so the `auth` callback below
    // hands the server a live token on the retry.
    try {
      await refreshAccessTokenSingleFlight();
    } catch (refreshError) {
      if (isDefinitiveAuthFailure(refreshError)) {
        // The server has rejected the refresh cookie: the session is over.
        // A long-lived page reconnecting after idle may run no HTTP request
        // that would notice, leaving the protected UI open (and the composer
        // emitting into a dead socket) indefinitely, so end the session here
        // instead of assuming another path will. Guarded so a teardown that
        // already happened while the refresh was in flight is not repeated.
        useSocketStore.getState().setStatus('disconnected');
        if (socket === target && useAuthStore.getState().hasSession) {
          handleDefinitiveAuthFailure();
        }
        return;
      }
      // Transient — fall through and retry with backoff.
    }
    // The refresh above is a real await: the user may have logged out (or
    // the socket may have been swapped for a new client) while it was in
    // flight. Reconnecting now would hand a freshly minted token to a
    // socket that belongs to a session that no longer exists.
    if (!useAuthStore.getState().hasSession) {
      useSocketStore.getState().setStatus('disconnected');
      return;
    }
  }

  // Torn down (logout / token swap) while this handler was running: the
  // new client owns the status now; do not flip it back to 'reconnecting'.
  if (socket !== target) return;

  scheduleConnectRetry(target);
}

export function getSocket(): Socket | null {
  return socket;
}

export function connectSocket(): Socket {
  if (socket?.connected) return socket;

  socket = io(import.meta.env.VITE_WS_URL || window.location.origin, {
    // Use a function so every connect/reconnect reads the current token
    // instead of capturing a stale one from the initial call (CWE-613).
    auth: (cb) => {
      cb({ token: useAuthStore.getState().accessToken });
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  const current = socket;

  socket.on('connect', () => {
    clearConnectRetry();
    useSocketStore.getState().setStatus('connected');
  });

  socket.on('disconnect', () => {
    useSocketStore.getState().setStatus('disconnected');
  });

  socket.on('connect_error', (err: Error) => {
    void handleConnectError(current, err);
  });

  socket.io.on('reconnect_attempt', () => {
    useSocketStore.getState().setStatus('reconnecting');
  });

  socket.io.on('reconnect', () => {
    useSocketStore.getState().setStatus('connected');
  });

  useSocketStore.getState().bumpSocketId();

  return socket;
}

/**
 * Re-run the handshake on the EXISTING client (listeners stay attached) —
 * used after MFA enrolment completes, when the server had rejected the
 * handshake with MFA_REQUIRED and nothing else changes the access token
 * that would otherwise make AppLayout rebuild the socket.
 */
export function reconnectSocket(): void {
  if (!socket || socket.connected) return;
  clearConnectRetry();
  useSocketStore.getState().setStatus('reconnecting');
  socket.connect();
}

export function disconnectSocket(): void {
  clearConnectRetry();
  if (socket) {
    socket.disconnect();
    socket = null;
    useSocketStore.getState().setStatus('disconnected');
  }
}
